import React, { forwardRef } from 'react';
import { cn } from '../../lib/utils';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'text';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  iconTrailing?: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  kbd?: string;
}

const variantStyles: Record<string, string> = {
  primary: 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500 shadow-card',
  secondary: 'bg-surface-overlay/85 backdrop-blur-xl text-content hover:bg-surface-overlay focus:ring-primary-500 border border-border',
  outline: 'border border-border bg-surface-raised/40 backdrop-blur-xl text-content hover:bg-surface-overlay/90 focus:ring-primary-500',
  ghost: 'bg-transparent text-content-secondary hover:bg-surface-overlay/90 hover:text-content focus:ring-primary-500',
  danger: 'bg-danger-500 text-white hover:bg-danger-500/90 focus:ring-danger-500 shadow-card',
  text: 'bg-transparent text-primary-500 hover:text-primary-600 hover:bg-primary-500/5 focus:ring-primary-500',
};

const sizeStyles: Record<string, string> = {
  xs: 'px-2.5 py-1.5 text-xs gap-1 rounded-full',
  sm: 'px-3.5 py-2 text-sm gap-1.5 rounded-full',
  md: 'px-4.5 py-2.5 text-sm gap-2 rounded-full',
  lg: 'px-6 py-3 text-base gap-2 rounded-full',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', icon, iconTrailing, loading, fullWidth, kbd, className, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface',
          'disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
        {children && <span>{children}</span>}
        {iconTrailing}
        {kbd && (
          <kbd className="ml-1 hidden rounded-full bg-white/12 px-2 py-1 text-[10px] font-mono sm:inline-block">
            {kbd}
          </kbd>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
